import { tokenAxios } from "@/utils/axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAppState } from "@/contexts/app-state";
import { Notes, CreateNoteArgs, OrderResponse } from "@/types/my-tasks";
import { OrdersQueryKey } from "@/types/order-table";
import { useBannerContext } from "@/contexts/banner-context";
import { buildApiUrl } from "@/utils/api";


interface PostNoteAxiosArgs extends CreateNoteArgs {
  practiceId: string;
  user: string;
}

interface GetNotesArgs {
  id: string;
  practiceId: string | undefined;
}

interface MutationOptions {
  onSettled: () => Promise<unknown>;
}

const getNotes = async ({ id, practiceId }: GetNotesArgs) => {
  // eslint-ignore-line
  const { data } = await tokenAxios().get<Notes[]>(buildApiUrl(`notes/${id}`), {
    params: { practiceId },
  });
  return data;
};

export const useNotes = (id: string) => {
  const { context } = useAppState();
  const practiceId = context?.athena?.practiceId ?? "8967";
  return useQuery<Notes[], Error>({
    queryKey: ["notes", id, practiceId],
    queryFn: () => getNotes({ id, practiceId }),
  });
};

const postNote = async (params: PostNoteAxiosArgs) => {
  return await tokenAxios().post(buildApiUrl("notes"), params);
};

export const useNotesMutation = () => {
  const { context, loggedInUser } = useAppState();
  const practiceId = context?.athena?.practiceId ?? "8967";
  const { isPending, isError, isSuccess, mutate } = useMutation({
    mutationFn: postNote,
  });



  const createNote = (params: CreateNoteArgs, options?: MutationOptions) =>
    mutate({
      ...params,
      user: loggedInUser,
      practiceId,
    }, {
      ...options
    });

  return {
    isPending,
    isError,
    isSuccess,
    createNote,
  };
};

interface AssignUserArgs {
  ordersQueryKey: OrdersQueryKey;
}

export const useAssignUser = ({ ordersQueryKey }: AssignUserArgs) => {
  const queryClient = useQueryClient();
  const { updateCallStatus } = useBannerContext();
  const { context, loggedInUser } = useAppState();
  const { isPending, isError, isSuccess, mutate } = useMutation({
    mutationFn: postNote,
    // https://tanstack.com/query/latest/docs/framework/react/guides/optimistic-updates#updating-a-list-of-todos-when-adding-a-new-todo
    onMutate: async (newValue) => {
      await queryClient.cancelQueries({ queryKey: ordersQueryKey });
      const previousOrders =
        queryClient.getQueryData<OrderResponse>(ordersQueryKey);

      queryClient.setQueryData<OrderResponse>(ordersQueryKey, (previous) => ({
        total: previous?.total ?? 0,
        rows: (previous?.rows ?? []).filter(
          (row) => newValue.orderIds?.includes(row.documentId),
        ),
        tabData: previous?.tabData ?? [],
        totalPages: previous?.totalPages ?? 0,
        totalCount: previous?.totalCount ?? 0,
      }));

      return { previousOrders };
    },
    onError: (error, newValue, context) => {
      queryClient.setQueryData(ordersQueryKey, context?.previousOrders);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ordersQueryKey });
    },
    onSuccess: () => {
      updateCallStatus({
        isAssignUserSucc: true
      });
    }
  });

  const practiceId = context?.athena?.practiceId ?? "8967";
  const assignUser = (params: CreateNoteArgs) => {
    return mutate({
      ...params,
      user: loggedInUser,
      practiceId,
    });
  };

  return {
    isPending,
    isError,
    isSuccess,
    assignUser,
  };
};



export const useAuthStatusChange = ({ ordersQueryKey }: AssignUserArgs) => {
  const queryClient = useQueryClient();
  const { context, loggedInUser } = useAppState();
  const { updateCallStatus } = useBannerContext();
  const { isPending, isError, isSuccess, mutate } = useMutation({
    mutationFn: postNote,
    // https://tanstack.com/query/latest/docs/framework/react/guides/optimistic-updates#updating-a-list-of-todos-when-adding-a-new-todo
    onMutate: async (newValue) => {
      await queryClient.cancelQueries({ queryKey: ordersQueryKey });
      const previousOrders =
        queryClient.getQueryData<OrderResponse>(ordersQueryKey);

      queryClient.setQueryData<OrderResponse>(ordersQueryKey, (previous) => ({
        total: previous?.total ?? 0,
        rows: (previous?.rows ?? []).filter(
          (row) => newValue.orderIds?.includes(row.documentId),
        ),
        tabData: previous?.tabData ?? [],
        totalPages: previous?.totalPages ?? 0,
        totalCount: previous?.totalCount ?? 0,
      }));

      return { previousOrders };
    },
    onError: (error, newValue, context) => {
      queryClient.setQueryData(ordersQueryKey, context?.previousOrders);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ordersQueryKey });
    },
    onSuccess: () => {
      updateCallStatus({
        isAuthStatusChangeSucc: true
      });
    }
  });

  const practiceId = context?.athena?.practiceId ?? "8967";
  const changeAuthStatus = (params: CreateNoteArgs) => {
    return mutate({
      ...params,
      user: loggedInUser,
      practiceId,
    });
  };

  return {
    isPending,
    isError,
    isSuccess,
    changeAuthStatus,
  };
};

export const useBucketReAssign = ({ ordersQueryKey }: AssignUserArgs) => {
  const queryClient = useQueryClient();
  const { updateCallStatus } = useBannerContext();
  const { context, loggedInUser } = useAppState();
  const { isPending, isError, isSuccess, mutate } = useMutation({
    mutationFn: postNote,
    // https://tanstack.com/query/latest/docs/framework/react/guides/optimistic-updates#updating-a-list-of-todos-when-adding-a-new-todo
    onMutate: async (newValue) => {
      await queryClient.cancelQueries({ queryKey: ordersQueryKey });
      const previousOrders =
        queryClient.getQueryData<OrderResponse>(ordersQueryKey);

      queryClient.setQueryData<OrderResponse>(ordersQueryKey, (previous) => ({
        total: previous?.total ?? 0,
        rows: (previous?.rows ?? []).filter(
          (row) => newValue.orderIds?.includes(row.documentId),
        ),
        tabData: previous?.tabData ?? [],
        totalPages: previous?.totalPages ?? 0,
        totalCount: previous?.totalCount ?? 0,
      }));

      return { previousOrders };
    },
    onError: (error, newValue, context) => {
      queryClient.setQueryData(ordersQueryKey, context?.previousOrders);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: ordersQueryKey });
    },
    onSuccess: () => {
      updateCallStatus({
        isUpdateBucketSucc: true
      });
    }
  });

  const practiceId = context?.athena?.practiceId ?? "8967";
  const bucketReAssign = (params: CreateNoteArgs) => {
    return mutate({
      ...params,
      user: loggedInUser,
      practiceId,
    });
  };

  return {
    isPending,
    isError,
    isSuccess,
    bucketReAssign,
  };
};


